import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  Sun,
  Moon,
  CircleNotch,
  X,
} from "@phosphor-icons/react";

const TZ = "Europe/Madrid";

function formatDateTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-ES", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: TZ,
  });
}

function formatTime(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: TZ,
  });
}

function todayISO() {
  const d = new Date();
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const y = parts.find((p) => p.type === "year").value;
  const m = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  return `${y}-${m}-${day}`;
}

function makeIsoInMadrid(dateStr, timeStr) {
  // dateStr: 2026-07-18, timeStr: 04:30 → ISO with proper Madrid offset
  // We need to determine the correct offset for that date.
  // Simple approach: build a Date via a UTC placeholder, then adjust with tz offset.
  const [y, mo, d] = dateStr.split("-").map((s) => parseInt(s, 10));
  const [h, mi] = timeStr.split(":").map((s) => parseInt(s, 10));
  // Build a datetime string as if it were UTC, then read what Madrid tz would show:
  // Instead of guessing, we ask Intl for the offset.
  const guess = new Date(Date.UTC(y, mo - 1, d, h, mi, 0));
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    timeZoneName: "shortOffset",
  }).formatToParts(guess);
  const off = parts.find((p) => p.type === "timeZoneName")?.value || "GMT+0";
  // parse "GMT+2" or "GMT+2:00" → hours
  const m2 = off.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  let offsetH = 0;
  let offsetM = 0;
  if (m2) {
    offsetH = parseInt(m2[2], 10) * (m2[1] === "+" ? 1 : -1);
    offsetM = parseInt(m2[3] || "0", 10) * (m2[1] === "+" ? 1 : -1);
  }
  const sign = offsetH >= 0 && offsetM >= 0 ? "+" : "-";
  const absH = Math.abs(offsetH).toString().padStart(2, "0");
  const absM = Math.abs(offsetM).toString().padStart(2, "0");
  return `${dateStr}T${timeStr.padStart(5, "0")}:00${sign}${absH}:${absM}`;
}

export default function ManualAmbient() {
  const [events, setEvents] = useState([]);
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Form state
  const [courtId, setCourtId] = useState("");
  const [dateStr, setDateStr] = useState(todayISO());
  const [startTime, setStartTime] = useState("04:00");
  const [endTime, setEndTime] = useState("05:00");

  const load = async () => {
    try {
      const [list, dashboard] = await Promise.all([
        api.manualAmbientList(),
        api.dashboard(),
      ]);
      setEvents(list);
      const c = dashboard.courts || [];
      setCourts(c);
      if (!courtId && c.length > 0) setCourtId(c[0].id);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openModal = () => {
    if (courts.length > 0) setCourtId(courts[0].id);
    setDateStr(todayISO());
    setStartTime("04:00");
    setEndTime("05:00");
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!dateStr || !startTime || !endTime) {
      toast.error("Rellena fecha y horas");
      return;
    }
    if (startTime === endTime) {
      toast.error("La hora de fin no puede ser igual a la de inicio");
      return;
    }
    // Build ISO datetimes. If endTime is EARLIER or EQUAL to startTime as text,
    // treat the end as the next calendar day — i.e. the franja crosses
    // midnight (e.g. 23:00 → 01:00 next day). Comparing as text ("01:00" <
    // "23:00") would otherwise block cross-midnight windows entirely.
    let endDateStr = dateStr;
    if (endTime <= startTime) {
      const [y, m, d] = dateStr.split("-").map((n) => parseInt(n, 10));
      const next = new Date(Date.UTC(y, m - 1, d + 1));
      endDateStr =
        `${next.getUTCFullYear()}-` +
        `${String(next.getUTCMonth() + 1).padStart(2, "0")}-` +
        `${String(next.getUTCDate()).padStart(2, "0")}`;
    }
    const startIso = makeIsoInMadrid(dateStr, startTime);
    const endIso = makeIsoInMadrid(endDateStr, endTime);
    if (new Date(endIso) <= new Date(startIso)) {
      toast.error("La hora de fin debe ser posterior a la de inicio");
      return;
    }
    setSaving(true);
    try {
      await api.manualAmbientCreate({
        court_id: courtId,
        start_at: startIso,
        end_at: endIso,
      });
      toast.success("Franja manual creada");
      setModalOpen(false);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Error al crear la franja";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api.manualAmbientDelete(id);
      toast.success("Franja eliminada");
      await load();
    } catch (err) {
      toast.error("Error al eliminar");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-3" data-testid="manual-ambient-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2">
            <Moon size={20} weight="bold" className="text-cyan-400" />
            Franjas manuales
            <span className="text-xs text-zinc-500 font-mono font-normal">Cualquier pista</span>
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Enciende cualquier pista en una franja horaria puntual (ej. madrugada), sin depender del anochecer.
          </p>
        </div>
        <button
          data-testid="manual-ambient-add-button"
          onClick={openModal}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-cyan-400 text-black text-sm font-bold hover:bg-cyan-300 transition-colors"
        >
          <Plus size={14} weight="bold" />
          Añadir
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-zinc-500">
          <CircleNotch size={20} className="animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div
          className="bg-[#121212] border border-zinc-800 rounded-md px-4 py-6 text-center text-sm text-zinc-500"
          data-testid="manual-ambient-empty"
        >
          No hay franjas manuales programadas.
        </div>
      ) : (
        <div className="bg-[#121212] border border-zinc-800 rounded-md overflow-hidden divide-y divide-zinc-900">
          {events.map((ev) => (
            <div
              key={ev.id}
              className="px-4 py-3 flex items-center justify-between gap-3"
              data-testid={`manual-ambient-item-${ev.id}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-cyan-400/10 flex items-center justify-center text-cyan-400 shrink-0">
                  <Sun size={16} weight="bold" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-bold truncate">
                    {courts.find((c) => c.id === ev.court_id)?.name || ev.court_id}
                  </div>
                  <div className="text-xs text-zinc-400 font-mono">
                    {formatDateTime(ev.start_at)} · {formatTime(ev.start_at)}–{formatTime(ev.end_at)}
                  </div>
                </div>
              </div>
              <button
                data-testid={`manual-ambient-delete-${ev.id}`}
                onClick={() => handleDelete(ev.id)}
                disabled={deletingId === ev.id}
                className="p-2 rounded hover:bg-red-500/10 text-red-400 hover:text-red-300 transition-colors disabled:opacity-50"
                title="Eliminar franja"
              >
                {deletingId === ev.id ? (
                  <CircleNotch size={16} className="animate-spin" />
                ) : (
                  <Trash size={16} weight="bold" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="manual-ambient-modal"
        >
          <div className="bg-[#0f0f0f] border border-zinc-800 rounded-lg w-full max-w-md">
            <header className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Nueva franja manual</h3>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1 rounded hover:bg-zinc-800 text-zinc-400"
                data-testid="manual-ambient-modal-close"
              >
                <X size={18} weight="bold" />
              </button>
            </header>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono block mb-1.5">
                  Pista
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {courts.map((c) => (
                    <button
                      key={c.id}
                      data-testid={`manual-ambient-court-${c.id}`}
                      onClick={() => setCourtId(c.id)}
                      className={`px-3 py-2 rounded border text-sm font-bold transition-colors ${
                        courtId === c.id
                          ? "bg-cyan-400 border-cyan-400 text-black"
                          : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700"
                      }`}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono block mb-1.5">
                  Fecha
                </label>
                <input
                  type="date"
                  value={dateStr}
                  onChange={(e) => setDateStr(e.target.value)}
                  className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-cyan-400"
                  data-testid="manual-ambient-date"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono block mb-1.5">
                    Hora inicio
                  </label>
                  <input
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-cyan-400"
                    data-testid="manual-ambient-start-time"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-zinc-500 uppercase tracking-widest font-mono block mb-1.5">
                    Hora fin
                  </label>
                  <input
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    className="w-full bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm font-mono focus:outline-none focus:border-cyan-400"
                    data-testid="manual-ambient-end-time"
                  />
                </div>
              </div>

              <div className="text-xs text-zinc-500 leading-relaxed">
                La luz se encenderá en la fecha y hora indicadas, y se apagará automáticamente
                al finalizar. Las franjas manuales <b>se añaden</b> a las automáticas (no las reemplazan).
              </div>
            </div>
            <footer className="px-5 py-4 border-t border-zinc-800 flex items-center justify-end gap-2">
              <button
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 rounded bg-zinc-900 border border-zinc-800 text-sm font-bold hover:bg-zinc-800 transition-colors"
                data-testid="manual-ambient-cancel"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded bg-cyan-400 text-black text-sm font-bold hover:bg-cyan-300 transition-colors disabled:opacity-60 flex items-center gap-2"
                data-testid="manual-ambient-save"
              >
                {saving && <CircleNotch size={14} className="animate-spin" />}
                Programar
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
